import type { Message } from '@/features/chat/chat.api';

/**
 * Delivery-status vocabulary plus the pure rules the cache write paths and
 * the bubble share — 2026-09 "honest send status" (F2; spec
 * `docs/specs/2026-09-features/02-honest-send-status.md` §3, §4.5, §4.6).
 *
 * Created by T0-F (contract plumbing); extended by T2-F:
 *   - the resendable set is `failed | unknown` — the hub's resend rule — and
 *     an `unknown` source needs the operator's duplicate-risk confirmation
 *     (`needsDuplicateRiskConfirm`, `ResendConfirmDialog`);
 *   - `mergeDelivery` also guards `mergeServerMessage`, so a late REST 201
 *     cannot regress a receipt either (02 §2 P7);
 *   - `describeDeliveryStatus` is the ONE status -> bubble-copy mapping
 *     (`DeliveryStatusIcon`) — an unrecognised status is never "sent"
 *     (bug 4, 02 §2 P5);
 *   - `isRestrictedFailure` spots WhatsApp's reach-out-timelock NACK, the
 *     failure a same-account retry cannot fix (02 §4.5).
 */

/**
 * The wire vocabulary of `MessageOut.delivery_status` and the realtime
 * `message.status` payload — Contract A `delivery.status`, i.e. the hub's
 * CHECK `ck_messages_delivery_status` after migration 0015 (spec 00 §3.1,
 * 00 §3.2 Contract A, 02 §3 / §5):
 *
 *   received | queued | sent | delivered | read | failed | unknown
 *
 * `read` and `unknown` are the two 0015 additions; the base-branch CHECK
 * (`hub/app/store/models.py`) and `Delivery.status` Literal
 * (`hub/app/domain/message.py`) read `received, queued, sent, delivered,
 * failed`.
 *
 * The zod schemas deliberately keep `delivery_status` a plain string:
 * anything outside this list is forward-compat and must still parse
 * (02 §3 "anything else" — rendered as a neutral question mark, never as a
 * "sent" check).
 */
export const DELIVERY_STATUSES = [
  'received',
  'queued',
  'sent',
  'delivered',
  'read',
  'failed',
  'unknown',
] as const;

export type KnownDeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** The client-only status of an optimistic placeholder
 *  (`buildOptimisticMessage` in `useSendMessage.ts`). Never on the wire —
 *  02 §3: "`pending` stays client-only". */
export const PENDING_DELIVERY_STATUS = 'pending';

/** Id prefix of an optimistic placeholder — mirrors `buildOptimisticMessage`
 *  (`optim-${clientRef}`). Such a "ghost" row was never persisted. */
const OPTIMISTIC_ID_PREFIX = 'optim-';

/** Source statuses `POST /api/v1/messages/{id}/resend` accepts — Contract C
 *  (`docs/contracts/hub-frontend-api.md`; spec 00 §3.2 resend row, 02 §4.5):
 *  `failed` — definitely NOT delivered — and `unknown` — in doubt, the
 *  customer MAY have it, so the hub also demands
 *  `confirm_duplicate_risk: true` (see {@link needsDuplicateRiskConfirm}). */
const RESENDABLE_STATUSES: ReadonlySet<string> = new Set(['failed', 'unknown']);

/**
 * Can this bubble be resent through `POST /messages/{id}/resend`?
 *
 * Only a REAL (persisted) outbound row in a resendable status qualifies.
 * Ghost rows (optimistic placeholders whose POST never landed, id
 * `optim-…`) have no server row to resend — their bubble keeps the ghost
 * re-POST retry (`useSendMessage`'s `retryOfMessageId` path).
 */
export function isResendable(
  message: Pick<Message, 'id' | 'direction' | 'delivery_status'>,
): boolean {
  if (message.direction !== 'outbound') return false;
  if (message.id.startsWith(OPTIMISTIC_ID_PREFIX)) return false;
  return RESENDABLE_STATUSES.has(message.delivery_status);
}

/**
 * Must the operator confirm the duplicate risk before this row is resent?
 *
 * `unknown` = the send is in doubt (the frame went out unacknowledged, the
 * hub restarted before a queued send was confirmed, or WhatsApp reported an
 * error after sending): the customer MAY already have the message, so a
 * resend can deliver it twice. The hub refuses such a resend without
 * `confirm_duplicate_risk: true` (409 `RESEND_CONFIRM_REQUIRED_DETAIL`), and
 * the console asks first — EVERY resend of an `unknown` row, same- or
 * cross-account, goes through `ResendConfirmDialog` (02 §4.5).
 */
export function needsDuplicateRiskConfirm(
  message: Pick<Message, 'delivery_status'>,
): boolean {
  return message.delivery_status === 'unknown';
}

/**
 * The hub's `delivery_error` for WhatsApp's reach-out-timelock NACK: the
 * account is restricted from STARTING conversations, and WhatsApp rejected
 * the message after the send. It is the one post-send error the hub treats
 * as definitive (`failed`; every other post-send error is `unknown`).
 * Verbatim from spec 02 §3.1 (receipt `error`, restricted -> `failed`) — the
 * hub's receipt store (`hub/app/store/receipts.py`, T2-H) writes it.
 */
export const RESTRICTED_DELIVERY_ERROR =
  'WhatsApp restricted this account from messaging (reach-out timelock)';

/** The explicit hint a restricted failure carries (spec 02 §1, §4.5): the
 *  number itself is locked, so the cure is ANOTHER account. */
export const RESTRICTED_RESEND_HINT = '该号被 WhatsApp 限制主动发消息，请用其他账号重发';

/** Did this outbound row fail because WhatsApp restricted its account
 *  (reach-out timelock)? Exact match on {@link RESTRICTED_DELIVERY_ERROR}. */
export function isRestrictedFailure(
  message: Pick<Message, 'direction' | 'delivery_status' | 'delivery_error'>,
): boolean {
  return (
    message.direction === 'outbound' &&
    message.delivery_status === 'failed' &&
    message.delivery_error === RESTRICTED_DELIVERY_ERROR
  );
}

/** The delivery fields one status write may carry. `delivery_status` is
 *  always present; the three 0015 side fields are optional — an older hub,
 *  or a locally-built row, omits them. Structurally satisfied by both a
 *  `Message` and a `message.status` payload. */
export type DeliveryFields = Pick<
  Message,
  'delivery_status' | 'delivery_error' | 'delivered_at' | 'read_at'
>;

/** Everything a stale write could try to put back over a receipt-confirmed
 *  row: 02 §4.6 "never move `delivered`/`read` back to
 *  `queued|sent|failed|unknown`", plus the client-only `pending`. */
const PRE_RECEIPT_STATUSES: ReadonlySet<string> = new Set([
  PENDING_DELIVERY_STATUS,
  'queued',
  'sent',
  'failed',
  'unknown',
]);

/**
 * Would writing `nextStatus` over `prevStatus` move a receipt-confirmed row
 * DOWN? Server-side nothing ever moves a row down from `delivered`/`read`
 * (02 §3.1 — receipts are applied in SQL `WHERE` clauses), so on the client
 * such a write can only be an out-of-order arrival: a late REST 201 `sent`
 * (02 §2 P7), or the send endpoint's own `sent` event published after the
 * receipt's `delivered`. `read -> delivered` counts as stale too — the hub
 * never applies a `delivered` receipt to a `read` row. Statuses outside the
 * vocabulary are NOT blocked (forward compat).
 */
export function isStaleDeliveryWrite(prevStatus: string, nextStatus: string): boolean {
  if (prevStatus === 'read') {
    return nextStatus === 'delivered' || PRE_RECEIPT_STATUSES.has(nextStatus);
  }
  if (prevStatus === 'delivered') {
    return PRE_RECEIPT_STATUSES.has(nextStatus);
  }
  return false;
}

/**
 * Merge an incoming delivery write (`next` — a `message.status` payload or a
 * full server `MessageOut`) over the cached row (`prev`), monotonically.
 * Returns ONLY the delivery fields; the caller spreads them onto its row.
 *
 *   - `delivery_status`: `next`'s, unless that would be a stale downgrade
 *     (see {@link isStaleDeliveryWrite}) — then `prev`'s.
 *   - `delivery_error`: on a stale write, `prev`'s. Otherwise `next`'s
 *     whenever `next` carries the key — an explicit `null` CLEARS it (a
 *     `delivered`/`read` receipt sets it NULL, 02 §3.1) — else `prev`'s
 *     (an older hub omits the key).
 *   - `delivered_at` / `read_at`: `next`'s when non-null, else `prev`'s.
 *     The hub only ever COALESCEs them (set once, never cleared), so a
 *     null/absent value means "not known by this write", never "cleared".
 */
export function mergeDelivery(prev: DeliveryFields, next: DeliveryFields): DeliveryFields {
  const stale = isStaleDeliveryWrite(prev.delivery_status, next.delivery_status);
  return {
    delivery_status: stale ? prev.delivery_status : next.delivery_status,
    delivery_error:
      stale || next.delivery_error === undefined
        ? prev.delivery_error
        : next.delivery_error,
    delivered_at: next.delivered_at ?? prev.delivered_at,
    read_at: next.read_at ?? prev.read_at,
  };
}

/**
 * How an OUTBOUND bubble draws its status: one kind per outbound state the
 * console knows, plus `other` for everything else — a forward-compat value
 * this build has never heard of, or the inbound-only `received` on an
 * outbound row. `other` is a neutral question mark, NEVER a check (bug 4:
 * the old footer icon fell back to the single "sent" check).
 */
export type DeliveryStatusKind =
  | 'pending'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'
  | 'unknown'
  | 'other';

export interface DeliveryStatusDescription {
  kind: DeliveryStatusKind;
  /** The status in one line (spec 02 §3 "Bubble" copy) — the icon's
   *  accessible name and the tooltip's first line. */
  label: string;
  /** Further tooltip lines (a read row's delivery time, the restricted
   *  hint); often empty. */
  details: string[];
}

/** Receipt / error times in the tooltip — absolute, locale-default (the same
 *  rendering as the bubble's timestamp tooltip). An unparseable value is
 *  shown verbatim rather than as "Invalid Date". */
export function formatDeliveryTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function withTime(
  text: string,
  iso: string | null | undefined,
  formatTime: (iso: string) => string,
): string {
  return iso ? `${text} · ${formatTime(iso)}` : text;
}

/**
 * Map a row's delivery fields to the bubble's status kind and copy — spec 02
 * §3 (the "Bubble" column), with the 0015 side fields where available:
 *
 *   pending   发送中…                      (client-only optimistic send)
 *   queued    排队中：网关暂未连接，恢复后自动发出
 *   sent      已发出
 *   delivered 已送达 · {delivered_at}
 *   read      已读 · {read_at}              (+ 已送达 · {delivered_at})
 *   failed    发送失败：{delivery_error}     (+ the restricted hint)
 *   unknown   发送结果未知：{delivery_error}。客户可能已收到——重发前请先在手机上核对
 *   other     状态：{raw status}
 *
 * A missing timestamp / error simply drops its part of the line.
 */
export function describeDeliveryStatus(
  fields: DeliveryFields,
  formatTime: (iso: string) => string = formatDeliveryTime,
): DeliveryStatusDescription {
  const error = fields.delivery_error ?? null;
  switch (fields.delivery_status) {
    case PENDING_DELIVERY_STATUS:
      return { kind: 'pending', label: '发送中…', details: [] };
    case 'queued':
      return {
        kind: 'queued',
        label: '排队中：网关暂未连接，恢复后自动发出',
        details: [],
      };
    case 'sent':
      return { kind: 'sent', label: '已发出', details: [] };
    case 'delivered':
      return {
        kind: 'delivered',
        label: withTime('已送达', fields.delivered_at, formatTime),
        details: [],
      };
    case 'read':
      return {
        kind: 'read',
        label: withTime('已读', fields.read_at, formatTime),
        details: fields.delivered_at
          ? [withTime('已送达', fields.delivered_at, formatTime)]
          : [],
      };
    case 'failed':
      return {
        kind: 'failed',
        label: error ? `发送失败：${error}` : '发送失败',
        details: error === RESTRICTED_DELIVERY_ERROR ? [RESTRICTED_RESEND_HINT] : [],
      };
    case 'unknown':
      return {
        kind: 'unknown',
        label:
          `${error ? `发送结果未知：${error}` : '发送结果未知'}` +
          '。客户可能已收到——重发前请先在手机上核对',
        details: [],
      };
    default:
      return { kind: 'other', label: `状态：${fields.delivery_status}`, details: [] };
  }
}
